import { useSignal } from "@preact/signals-react";
import { useState, useRef } from "react";
import { FormattedMessage as FM } from "react-intl";
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";

import Dialog from "componentsv2/modal/Dialog";
import CustomForm from "componentsv2/form/CustomForm";
import Popover from "componentsv2/widgets/Popover";
import MenuActionReport from "componentsv2/menu/report/MenuActionReport";
import { DocumentIcon } from "components/Icons";
import {
  MessageContainer,
  useMessage,
} from "componentsv2/experimental/Message";
import { useWizard } from "storev2/wizardStore";
import funcs from "tools/functions";
import proxy from "api/proxy";
import { getStorage } from "store/local";

function DetailedDeliveryManReport() {
  const openModal = useSignal(false);
  const showPop = useSignal(false);
  const selectedParties = useSignal([]);
  const [tempParty, setTempParty] = useState(null);
  const [partyQuery, setPartyQuery] = useState("");
  const [partyResults, setPartyResults] = useState([]);
  const searchTimer = useRef(null);
  const session = getStorage("ctxSession");
  const { messages, error: setError, hide } = useMessage();
  const { store, active } = useWizard();
  const record = { store, active };
  console.log(
    "Deprecation warning......, NO USE MenuActionReport use MenuButton instead!",
  );

  const layout = {
    model: "sale_pos_frontend.detailed_deliveryman.start",
    reportName: "sale_pos_frontend.detailed_deliveryman_report",
    fields: {
      shop: {
        type: "many2one",
        model: "sale.shop",
        searchable: true,
        required: true,
        recSearch: () => [["company", "=", session.company]],
        default: session.shop_
          ? { id: session.shop_.id, name: session.shop_.name }
          : null,
      },
      id: {
        type: "integer",
        default: -1,
        invisible: () => true,
      },
      sale_date: {
        type: "date",
        required: true,
        default: () => new Date(),
      },
    },
    list: [],
    form: [{ name: "shop" }, { name: "sale_date" }],
    actionReport: async (data) => {
      const reportData = {
        sale_date: data.sale_date,
        shop: data.shop?.id || session.shop,
        shop_name: data.shop?.rec_name || session.shop_?.name || "",
        company_name: session.company_name,
      };
      if (selectedParties.value.length) {
        reportData.delivery_parties = selectedParties.value.map((p) => p.id);
      }
      return reportData;
    },
  };

  const reports = [
    {
      name: "sale_pos_frontend.detailed_deliveryman_report",
      description: "sale_pos_frontend.detailed_deliveryman_report",
      layout,
    },
  ];

  async function handlePartySearch(text) {
    setPartyQuery(text);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (text.length < 2) {
      setPartyResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      const { data } = await proxy.search(
        "sale.delivery_party",
        [["rec_name", "ilike", `%${text}%`]],
        ["rec_name"],
      );
      setPartyResults(data || []);
    }, 300);
  }

  function addParty() {
    if (
      tempParty &&
      !selectedParties.value.find((p) => p.id === tempParty.id)
    ) {
      selectedParties.value = [...selectedParties.value, tempParty];
    }
    setTempParty(null);
    setPartyQuery("");
    setPartyResults([]);
  }

  function removeParty(id) {
    selectedParties.value = selectedParties.value.filter((p) => p.id !== id);
  }

  async function onPrintReport() {
    let data = null;
    if (layout.actionReport) {
      try {
        data = await layout.actionReport(record.store);
      } catch (err) {
        setError(err);
        return;
      }
    } else {
      data = record.store;
    }
    const storeStd = funcs.recToTryton(data);
    const { reportUrl, nameFile, error } = await proxy.report({
      report: layout.reportName,
      data: storeStd,
      records: [],
    });
    if (error) {
      setError(error);
    } else {
      funcs.createReportLink(reportUrl, nameFile);
      openModal.value = false;
    }
  }

  async function onSelectReport(report) {
    const { newRecord } = useWizard.getState();
    await newRecord(report.layout || layout);
    openModal.value = true;
    showPop.value = false;
  }

  const buttonPrint = {
    label: "button.print",
    name: "print",
    onClick: onPrintReport,
  };

  const buttonClose = {
    label: "button.close",
    name: "close",
    onClick: () => {
      openModal.value = false;
    },
  };

  const buttons = [buttonClose, buttonPrint];

  return (
    <div className="overflow-visible">
      <button
        type="button"
        onClick={() => (showPop.value = true)}
        className="flex border border-neutral-300 w-full md:w-32 h-11 items-center text-center bg-white py-2 px-3 text-neutral-600 rounded-xl hover:bg-neutral-200"
      >
        <span className="ml-auto mr-3 my-auto text-sm">
          <FM id="delivery.panel.reports" defaultMessage="Reports" />
        </span>
        <DocumentIcon className="mr-auto h-6 w-6 my-auto fill-current text-neutral-500" />
      </button>
      <Popover show={showPop} trigger="hover">
        {reports.map((report, idx) => (
          <MenuActionReport
            key={idx}
            report={report}
            handleClick={onSelectReport}
          />
        ))}
      </Popover>
      {openModal.value && (
        <Dialog
          openModal={openModal}
          handleClose={() => {
            openModal.value = false;
          }}
          title={layout.reportName}
          buttons={buttons}
          size="w-screen h-3/5 md:w-3/5"
          fixedHeight={true}
        >
          <MessageContainer messages={messages} onMessageClose={hide} />

          <div className="mt-5">
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Agregar Domiciliarios
            </label>
            <Combobox value={tempParty} onChange={setTempParty}>
              <div className="flex gap-2">
                <div className="relative w-full">
                  <ComboboxInput
                    onChange={(e) => handlePartySearch(e.target.value)}
                    displayValue={(p) => p?.rec_name || ""}
                    placeholder="Buscar domiciliario..."
                    className="appearance-none pt-2 pb-2 px-2 w-full bg-white border border-neutral-300 rounded-md"
                  />
                  <ComboboxOptions className="absolute w-full mt-1 border border-neutral-300 rounded-md bg-white max-h-40 overflow-y-auto z-10">
                    {partyResults.map((p) => (
                      <ComboboxOption key={p.id} value={p}>
                        {({ focus }) => (
                          <div
                            className={`px-3 py-2 cursor-pointer text-sm ${
                              focus ? "bg-sky-100" : ""
                            }`}
                          >
                            {p.rec_name}
                          </div>
                        )}
                      </ComboboxOption>
                    ))}
                  </ComboboxOptions>
                </div>
                <button
                  onClick={addParty}
                  className="w-16 px-2 py-1 bg-black text-white rounded-md text-md font-medium hover:bg-neutral-800 shrink-0"
                >
                  +
                </button>
              </div>
            </Combobox>
            {selectedParties.value.length > 0 && (
              <ul className="mt-3 space-y-1">
                {selectedParties.value.map((p) => (
                  <li
                    key={p.id}
                    className="flex items-center justify-between px-3 py-2 bg-white border border-neutral-200 rounded-md text-sm"
                  >
                    <span>{p.rec_name}</span>
                    <button
                      onClick={() => removeParty(p.id)}
                      className="text-rose-500 hover:text-rose-700 font-bold ml-2"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <CustomForm layout={layout} record={record} />
        </Dialog>
      )}
    </div>
  );
}

export default DetailedDeliveryManReport;
